/**
 * Frame capture from a Guacamole canvas.
 *
 * captureFrameBlob  — snapshot the current display as a JPEG Blob (q=0.78).
 * startCaptureLoop  — drive periodic capture, firing onFrame(timestampMs, blob).
 */

const JPEG_QUALITY = 0.78;

/**
 * Resolve the source canvas from a live Guacamole client.
 * Prefers the default layer canvas; falls back to the largest <canvas> inside
 * the display element.
 *
 * @param {object} guac - Guacamole.Client instance
 * @returns {HTMLCanvasElement}
 * @throws {Error} if no canvas can be found
 */
export function resolveSourceCanvas(guac) {
  try {
    const canvas = guac.getDisplay().getDefaultLayer().getCanvas();
    if (canvas) return canvas;
  } catch (_) {
    // Intentional: fall through to the DOM search below
  }

  const candidates = Array.from(
    guac.getDisplay().getElement().querySelectorAll("canvas")
  ).filter((c) => c.width > 0 && c.height > 0);
  if (candidates.length === 0) throw new Error("no usable source canvas");

  // Pick the largest canvas by pixel count
  return candidates.reduce((best, c) =>
    c.width * c.height > best.width * best.height ? c : best
  );
}

/**
 * Capture the current Guacamole display as a JPEG Blob at quality 0.78.
 *
 * @param {object} guac - Guacamole.Client instance
 * @returns {Promise<Blob>}
 */
export async function captureFrameBlob(guac) {
  const src = resolveSourceCanvas(guac);

  const offscreen = document.createElement("canvas");
  offscreen.width = src.width;
  offscreen.height = src.height;

  const ctx = offscreen.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0);

  return new Promise((resolve, reject) => {
    offscreen.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("toBlob produced null"));
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

/**
 * Start a periodic JPEG capture loop.
 *
 * Fires onFrame(timestampMs, blob) immediately at t=0, then once every
 * intervalMs thereafter. Per-frame errors are swallowed (console.warn) so a
 * transient canvas glitch does not abort the recording.
 *
 * @param {object} options
 * @param {object}   options.guac        - Guacamole.Client instance
 * @param {number}   options.intervalMs  - milliseconds between frames
 * @param {Function} options.onFrame     - callback(timestampMs, blob)
 * @returns {{ stop: Function }}
 */
export function startCaptureLoop({ guac, intervalMs, onFrame }) {
  let stopped = false;
  let nextFrameMs = 0;

  async function fire() {
    if (stopped) return;
    try {
      const blob = await captureFrameBlob(guac);
      if (!stopped) onFrame(nextFrameMs, blob);
    } catch (err) {
      console.warn("[frameCapture] frame skipped:", err);
    }
  }

  // First frame at t=0
  fire();

  const timerId = setInterval(() => {
    nextFrameMs += intervalMs;
    fire();
  }, intervalMs);

  return {
    stop() {
      stopped = true;
      clearInterval(timerId);
    },
  };
}
